export function isSafeRelativeReturnUrl(returnUrl: string | null | undefined): returnUrl is string {
  if (!returnUrl) return false

  return (
    returnUrl.startsWith('/') &&
    !returnUrl.startsWith('//') &&
    !returnUrl.startsWith('/\\') &&
    !returnUrl.includes('\\')
  )
}

export function getSafeRelativeReturnUrl(returnUrl: string | null | undefined, fallback = '/'): string {
  return isSafeRelativeReturnUrl(returnUrl) ? returnUrl : fallback
}

const AUTH_RETURN_PATHS = new Set([
  '/login',
  '/login/callback',
  '/auth/employee-sso',
  '/email-check',
  '/register',
  '/verify-email',
  '/forgot-password',
  '/reset-password',
  '/account-setup',
])

export function getSafePostAuthReturnUrl(returnUrl: string | null | undefined, fallback = '/'): string {
  const safeReturnUrl = getSafeRelativeReturnUrl(returnUrl, fallback)
  const pathname = safeReturnUrl.split(/[?#]/, 1)[0]

  return AUTH_RETURN_PATHS.has(pathname) ? fallback : safeReturnUrl
}

// Employee Login leaves for the admin portal (same origin, often a new tab) and
// account creation ends on a fresh /login visit, so router state can't carry the
// destination. Stored in localStorage so the tab the admin portal opens can read it.
const POST_AUTH_RETURN_STORAGE_KEY = 'postAuthReturn'
const POST_AUTH_RETURN_TTL_MS = 30 * 60 * 1000
export const RESUME_PENDING_ACTION_PATH = '/login/callback'

export function rememberPostAuthReturnUrl(returnUrl: string | null | undefined): void {
  try {
    if (!isSafeRelativeReturnUrl(returnUrl)) {
      localStorage.removeItem(POST_AUTH_RETURN_STORAGE_KEY)
      return
    }
    localStorage.setItem(POST_AUTH_RETURN_STORAGE_KEY, JSON.stringify({ url: returnUrl, savedAt: Date.now() }))
  } catch { /* Storage unavailable; sign-in falls back to the default page. */ }
}

export function takePostAuthReturnUrl(): string | undefined {
  let stored: string | null = null
  try {
    stored = localStorage.getItem(POST_AUTH_RETURN_STORAGE_KEY)
    localStorage.removeItem(POST_AUTH_RETURN_STORAGE_KEY)
  } catch {
    return undefined
  }
  if (!stored) return undefined

  try {
    const { url, savedAt } = JSON.parse(stored) as { url?: unknown; savedAt?: unknown }
    if (typeof url !== 'string' || typeof savedAt !== 'number') return undefined
    if (Date.now() - savedAt > POST_AUTH_RETURN_TTL_MS) return undefined
    if (url === RESUME_PENDING_ACTION_PATH) return url
    return getSafePostAuthReturnUrl(url, '') || undefined
  } catch {
    return undefined
  }
}
